import { useState } from 'react'
import { RANGE_PRESETS, type RangeChoice } from './format'

/** A per-viewer setting kept in localStorage; it falls back quietly when storage is unavailable. */
export function useStored<T>(key: string, fallback: T, valid: (v: unknown) => v is T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      const parsed: unknown = raw == null ? null : JSON.parse(raw)
      return valid(parsed) ? parsed : fallback
    } catch {
      return fallback
    }
  })
  // A new key (another tab of the page) reads its own value.
  const [shownKey, setShownKey] = useState(key)
  if (shownKey !== key) {
    setShownKey(key)
    try {
      const raw = localStorage.getItem(key)
      const parsed: unknown = raw == null ? null : JSON.parse(raw)
      setValue(valid(parsed) ? parsed : fallback)
    } catch {
      setValue(fallback)
    }
  }
  const set = (v: T) => {
    setValue(v)
    try {
      localStorage.setItem(key, JSON.stringify(v))
    } catch {
      // per-viewer convenience only
    }
  }
  return [value, set]
}

export const isRangeChoice = (v: unknown): v is RangeChoice =>
  typeof v === 'object' && v != null && RANGE_PRESETS.includes((v as RangeChoice).preset) && typeof (v as RangeChoice).custom === 'object'
